const BiotechUnit2Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-indigo-900 mb-3 border-b-2 border-slate-200 pb-2">Unit II: Recombinant DNA Technology & PCR</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit dives deep into the core tools of Genetic Engineering, detailing how recombinant DNA (rDNA) is constructed and practically applied in medicine to produce life-saving therapeutics like human insulin, vaccines, and interferons.
        </p>
    </div>

    <!-- 2. Tools of rDNA Technology -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">1. Study of Cloning Vectors, Restriction Endonucleases & DNA Ligase</h3>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <!-- Restriction Endonucleases -->
            <div class="bg-indigo-50 p-4 border border-indigo-100 rounded-lg shadow-sm">
                <h4 class="text-[15px] font-bold text-indigo-900 mb-2 flex items-center gap-2">✂️ Restriction Endonucleases</h4>
                <p class="text-[12px] text-slate-700 mb-2">Often called "Molecular Scissors". These are bacterial enzymes that recognize specific DNA sequences (usually <strong>Palindromic sequences</strong>, reading the same forwards and backwards) and cut the DNA at those precise sites.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li><strong>Type II</strong> enzymes are exclusively used in rDNA technology because they cut within the recognition sequence without needing ATP.</li>
                    <li><em>Example:</em> <strong>EcoRI</strong> (from E. coli) recognizes the sequence <code>GAATTC</code> and produces <strong>"sticky ends"</strong> (overhangs) which are crucial for joining foreign DNA.</li>
                </ul>
            </div>

            <!-- DNA Ligase -->
            <div class="bg-indigo-50 p-4 border border-indigo-100 rounded-lg shadow-sm">
                <h4 class="text-[15px] font-bold text-indigo-900 mb-2 flex items-center gap-2">🧬 DNA Ligase</h4>
                <p class="text-[12px] text-slate-700 mb-2">Known as "Molecular Glue". This enzyme joins two DNA fragments together by forming a <strong>phosphodiester bond</strong> between the 3'-OH group of one fragment and the 5'-phosphate group of the other.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>It permanently seals the gaps in the recombinant DNA backbone.</li>
                    <li><em>Example:</em> <strong>T4 DNA Ligase</strong> (derived from the T4 bacteriophage) is most widely used because it can join both sticky and blunt ends. Requires ATP.</li>
                </ul>
            </div>
        </div>

        <!-- Cloning Vectors -->
        <div>
            <h4 class="text-[16px] font-bold text-indigo-900 mb-3">Cloning Vectors</h4>
            <p class="text-[13px] text-slate-700 mb-3">Vectors are DNA molecules used as "vehicles" to carry foreign genetic material into another cell, where it can be replicated and/or expressed. Ideal features include an <strong>Origin of Replication (ori)</strong>, <strong>Selectable Marker (e.g., Antibiotic resistance gene)</strong>, and a <strong>Multiple Cloning Site (MCS)</strong>.</p>
            
            <div class="overflow-x-auto rounded-lg border border-slate-200">
                <table class="min-w-full bg-white text-[13px] text-left">
                    <thead class="bg-slate-100 text-slate-700">
                        <tr>
                            <th class="px-4 py-2.5 font-bold w-1/4">Vector Type</th>
                            <th class="px-4 py-2.5 font-bold w-1/4">Insert Capacity</th>
                            <th class="px-4 py-2.5 font-bold w-1/2">Key Characteristics & Examples</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-slate-600">
                        <tr>
                            <td class="px-4 py-3 font-bold text-indigo-800">Plasmids</td>
                            <td class="px-4 py-3">0.1 - 10 kb</td>
                            <td class="px-4 py-3">Extra-chromosomal, circular, double-stranded DNA found naturally in bacteria. <em>Example: pBR322, pUC19.</em> Ideal for routine cloning.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-indigo-800">Bacteriophages</td>
                            <td class="px-4 py-3">10 - 20 kb</td>
                            <td class="px-4 py-3">Viruses that infect bacteria. Highly efficient in transferring DNA into bacterial host cells. <em>Example: Lambda Phage.</em></td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-indigo-800">Cosmids</td>
                            <td class="px-4 py-3">35 - 45 kb</td>
                            <td class="px-4 py-3">Hybrid vectors combining features of both plasmids and phage (contains the 'cos' site). Useful for genomic libraries.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-indigo-800">BACs & YACs</td>
                            <td class="px-4 py-3">100 - 1000 kb</td>
                            <td class="px-4 py-3">Bacterial/Yeast Artificial Chromosomes. Used for cloning massive DNA fragments, like in the Human Genome Project.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- 3. Production of r-DNA Therapeutics -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">2. Production of Therapeutics via rDNA Technology</h3>
        <p class="text-[13px] text-slate-700 mb-5">Genetic engineering is revolutionary in manufacturing safe, abundant, and pure biological medicines that were previously extracted from animal or human sources.</p>

        <div class="space-y-6">
            <!-- Recombinant Human Insulin -->
            <div class="border border-teal-200 p-5 rounded-lg bg-teal-50">
                <h4 class="text-[16px] font-bold text-teal-900 mb-2">A. Human Insulin (Humulin)</h4>
                <p class="text-[12px] text-slate-700 mb-3">Human insulin consists of two short polypeptide chains: Chain A (21 amino acids) and Chain B (30 amino acids) linked by disulfide bonds. Earlier, insulin was extracted from pig/cow pancreas, causing severe allergic reactions.</p>
                <div class="bg-white p-3 rounded border border-teal-100 text-[12px] text-slate-600">
                    <p class="font-bold text-teal-800 mb-1">Production Steps (Eli Lilly Method):</p>
                    <ol class="list-decimal pl-5 space-y-1">
                        <li>DNA sequences for Chain A and Chain B are artificially synthesized in the lab.</li>
                        <li>Each gene is inserted separately into a plasmid vector (pBR322) adjacent to the <em>&beta;-galactosidase</em> (lacZ) gene.</li>
                        <li>The recombinant plasmids are transformed into two separate batches of <em>E. coli</em> bacteria.</li>
                        <li>The bacteria act as factories, producing a fusion protein (&beta;-galactosidase + Insulin chain).</li>
                        <li>The fusion proteins are extracted and treated with <strong>Cyanogen Bromide (CNBr)</strong> to cleave off the &beta;-galactosidase, releasing pure Chain A and Chain B.</li>
                        <li>Finally, Chain A and Chain B are mixed in an oxidizing environment to form natural <strong>disulfide bonds</strong>, creating functional human insulin.</li>
                    </ol>
                </div>
            </div>

            <!-- Hepatitis B Vaccine -->
            <div class="border border-blue-200 p-5 rounded-lg bg-blue-50">
                <h4 class="text-[16px] font-bold text-blue-900 mb-2">B. Hepatitis-B Vaccine (Recombinant Subunit Vaccine)</h4>
                <p class="text-[12px] text-slate-700 mb-3">It is the first commercially available recombinant vaccine. Unlike traditional vaccines containing killed or weakened viruses, this uses only a harmless surface protein (antigen) of the virus, making it 100% safe.</p>
                <div class="bg-white p-3 rounded border border-blue-100 text-[12px] text-slate-600">
                    <p class="font-bold text-blue-800 mb-1">Production Steps:</p>
                    <ol class="list-decimal pl-5 space-y-1">
                        <li>The gene encoding the <strong>Hepatitis B surface Antigen (HBsAg)</strong> is isolated from the viral genome.</li>
                        <li>The gene is inserted into an expression plasmid vector.</li>
                        <li>The vector is transformed into <em>Saccharomyces cerevisiae</em> (Baker's Yeast), not E. coli, because yeast can perform necessary post-translational modifications.</li>
                        <li>The yeast is cultured in large fermenters where it produces massive amounts of HBsAg protein.</li>
                        <li>Yeast cells are lysed (broken), and the HBsAg protein is highly purified and formulated with an adjuvant (like alum) into the final vaccine.</li>
                    </ol>
                </div>
            </div>

            <!-- Interferon -->
            <div class="border border-purple-200 p-5 rounded-lg bg-purple-50">
                <h4 class="text-[16px] font-bold text-purple-900 mb-2">C. Interferons (IFN)</h4>
                <p class="text-[12px] text-slate-700 mb-3">Interferons are highly potent cytokines produced by host cells in response to viral infections. They inhibit viral replication and modulate the immune system. (e.g., IFN-&alpha; used for Hepatitis C and some cancers).</p>
                <div class="bg-white p-3 rounded border border-purple-100 text-[12px] text-slate-600">
                    <p class="font-bold text-purple-800 mb-1">Production Steps:</p>
                    <ol class="list-decimal pl-5 space-y-1">
                        <li>Messenger RNA (mRNA) specific to human interferon is isolated from human leukocytes (WBCs).</li>
                        <li>The enzyme <strong>Reverse Transcriptase</strong> is used to convert this mRNA into complementary DNA (cDNA).</li>
                        <li>The cDNA is inserted into a bacterial expression vector (plasmid).</li>
                        <li>The vector is introduced into <em>E. coli</em>.</li>
                        <li>The recombinant <em>E. coli</em> is fermented, expressing large quantities of recombinant interferon, which is then extracted and purified using affinity chromatography.</li>
                    </ol>
                </div>
            </div>
        </div>
    </div>

    <!-- 4. Polymerase Chain Reaction (PCR) -->
    <div class="bg-slate-800 text-white p-6 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-white mb-3">3. Polymerase Chain Reaction (PCR)</h3>
        <p class="text-[13px] text-slate-300 mb-4 leading-relaxed">
            Invented by Kary Mullis in 1983, PCR is an in-vitro technique used to exponentially amplify (make millions of copies) a specific target DNA segment in a very short time. It is essentially "molecular photocopying".
        </p>

        <!-- Components -->
        <div class="bg-slate-700 p-4 rounded-lg mb-5 text-[13px]">
            <p class="font-bold text-slate-200 mb-2">Essential Components:</p>
            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-300">
                <li>🧬 <strong>Template DNA:</strong> The DNA segment to be amplified.</li>
                <li>🎯 <strong>Primers:</strong> Short synthetic RNA/DNA strands complementary to the target sequence ends.</li>
                <li>🔥 <strong>Taq Polymerase:</strong> A thermostable DNA polymerase isolated from the hot-spring bacterium <em>Thermus aquaticus</em>.</li>
                <li>🧱 <strong>dNTPs:</strong> Free nucleotides (A, T, G, C) acting as building blocks.</li>
            </ul>
        </div>

        <!-- 3 Steps of PCR -->
        <h4 class="text-[15px] font-bold text-slate-200 mb-3">The 3 Core Steps of PCR (One Cycle):</h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
            <div class="bg-slate-900 p-4 rounded border border-slate-600">
                <p class="font-bold text-red-400 mb-1">1. Denaturation (94-96°C)</p>
                <p class="text-[12px] text-slate-400">High heat breaks the hydrogen bonds between the double-stranded DNA, separating it into two single strands.</p>
            </div>
            <div class="bg-slate-900 p-4 rounded border border-slate-600">
                <p class="font-bold text-blue-400 mb-1">2. Annealing (50-65°C)</p>
                <p class="text-[12px] text-slate-400">Temperature is lowered to allow the specifically designed primers to attach (anneal) to their complementary sequences on the single-stranded DNA.</p>
            </div>
            <div class="bg-slate-900 p-4 rounded border border-slate-600">
                <p class="font-bold text-green-400 mb-1">3. Extension (72°C)</p>
                <p class="text-[12px] text-slate-400">Optimal temp for Taq Polymerase. It binds to the primer and adds dNTPs to synthesize a new complementary DNA strand.</p>
            </div>
        </div>
        <p class="text-[12px] text-slate-400 mt-3 text-center italic">This cycle is repeated 25-35 times in a thermocycler, resulting in over a billion copies of the target DNA.</p>
    </div>

</div>
`;

export default BiotechUnit2Notes;